import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useApplyUplift, useTrainingBoard, useTrainingEvent } from "../../lib/api";
import { SectionCard, Stat } from "../labour/shared";

const STATE_CLS: Record<string, string> = {
  fresh: "text-status-green", ageing: "text-status-amber", stale: "text-status-red", never: "text-brand-grey",
};

/** Training outcomes (HRX-09): whether anybody learned anything.
 *
 * Training happens here — eight programmes, eleven events, 45 people marked present. What did not happen was
 * any record of the outcome: `Training Result` and `Training Feedback` both held zero rows, so somebody sat
 * through First Aid and Lock-out for a full day and nothing said whether they could isolate a machine.
 *
 * The consequence runs further than it looks. The competency matrix and the redeployment search read employee
 * skill proficiencies, and no course had ever moved one — so the matrix described the workforce as it was
 * assessed once and never since. The freshness figure below is the honest measure of how much of it is still
 * worth believing. */
export default function Outcomes({ company }: { company?: string }) {
  const { t } = useTranslation();
  const q = useTrainingBoard(company);
  const [open, setOpen] = useState<string | null>(null);

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;
  const failing = d.state.filter((s) => !s.ok);

  return (
    <div className="mt-4 space-y-4" data-training-outcomes>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-training-strip>
        <Stat label={t("tro.attended")} value={d.summary.attended}
          hint={t("tro.attendedHint", { events: d.summary.completed, absent: d.summary.no_shows })} />
        <Stat label={t("tro.graded")} value={d.summary.graded}
          cls={d.summary.graded ? "text-status-green" : "text-status-amber"} hint={t("tro.gradedHint")} />
        <Stat label={t("tro.ungraded")} value={d.summary.ungraded}
          cls={d.summary.ungraded ? "text-status-amber" : ""} hint={t("tro.ungradedHint")} />
        <Stat label={t("tro.neverAsked")} value={d.summary.never_asked}
          cls={d.summary.never_asked ? "text-status-amber" : ""} hint={t("tro.neverAskedHint")} />
        <Stat label={t("tro.matrixFresh")} value={`${d.matrix.trustworthy_pct} %`}
          cls={d.matrix.trustworthy_pct < 60 ? "text-status-amber" : "text-status-green"}
          hint={t("tro.matrixFreshHint", { n: d.matrix.skills })} />
      </div>

      <SectionCard title={t("tro.matrixAge")} aside={t("tro.matrixAgeHint")}>
        <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-4" data-training-matrix>
          {(["fresh", "ageing", "stale", "never"] as const).map((k) => (
            <div key={k} data-training-matrix-band={k}>
              <div className={`font-heading text-xl font-semibold ${STATE_CLS[k]}`}>{d.matrix[k]}</div>
              <div className="text-[11px] uppercase tracking-wide text-brand-grey">{t(`tro.age.${k}`)}</div>
            </div>
          ))}
        </div>
        {d.matrix.oldest && (
          <p className="border-t border-brand-grey-light bg-[#F6F8FA] px-4 py-2 text-[11px] text-brand-grey"
            data-training-oldest>
            {t("tro.oldest", { skill: d.matrix.oldest.skill ?? "", who: d.matrix.oldest.employee_name ?? "",
                               days: d.matrix.oldest.days ?? 0 })}
          </p>
        )}
      </SectionCard>

      {failing.length > 0 && (
        <SectionCard title={t("tro.findings")} aside={t("tro.findingsN", { n: failing.length, of: d.state.length })}>
          <ul>
            {failing.map((s) => (
              <li key={s.key} className="flex items-start gap-2 border-t border-brand-grey-light px-4 py-3 first:border-t-0"
                data-training-finding={s.key}>
                <span className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-status-amber" />
                <div>
                  <div className="font-semibold">{t(`tro.check.${s.key}.title`)}</div>
                  <p className="mt-0.5 max-w-4xl text-sm text-brand-grey">{t(`tro.check.${s.key}.bad`, s.facts) as string}</p>
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      <SectionCard title={t("tro.events")} aside={t("tro.eventsN", { n: d.events.length })}>
        <ul>
          {d.events.map((e) => (
            <li key={e.name} className="border-t border-brand-grey-light first:border-t-0" data-training-event={e.name}>
              <button onClick={() => setOpen(open === e.name ? null : e.name)}
                className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[#F6F8FA]"
                data-training-open={e.name}>
                <div className="min-w-0">
                  <div className="font-medium">{e.event_name}</div>
                  <div className="text-[11px] text-brand-grey">
                    {e.training_program} · {e.company} · {String(e.start_time ?? "").slice(0, 10)}
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[11px]">
                  <span className="text-brand-grey">
                    {t("tro.presentN", { present: e.attendance.present, absent: e.attendance.absent })}
                  </span>
                  {e.closed ? (
                    e.results.graded ? (
                      <span className="rounded bg-green-50 px-2 py-0.5 font-semibold text-status-green">
                        {t("tro.passRate", { n: e.results.pass_rate, avg: e.results.average_score })}
                      </span>
                    ) : (
                      <span className="rounded bg-amber-50 px-2 py-0.5 font-semibold text-status-amber">
                        {t("tro.noResult")}
                      </span>
                    )
                  ) : (
                    <span className="rounded bg-brand-grey-light px-2 py-0.5 font-semibold text-brand-grey">{e.event_status}</span>
                  )}
                  {e.closed && e.feedback.given === 0 && (
                    <span className="rounded bg-amber-50 px-2 py-0.5 font-semibold text-status-amber">{t("tro.noFeedback")}</span>
                  )}
                </div>
              </button>
              {open === e.name && <Detail name={e.name} />}
            </li>
          ))}
        </ul>
      </SectionCard>
    </div>
  );
}

function Detail({ name }: { name: string }) {
  const { t } = useTranslation();
  const q = useTrainingEvent(name);
  const uplift = useApplyUplift();

  if (q.isError) return <div className="p-4"><ErrorBox error={q.error} /></div>;
  if (!q.data) return <Skeleton className="m-4 h-32" />;
  const d = q.data;

  return (
    <div className="border-t border-brand-grey-light bg-[#F6F8FA] p-4" data-training-detail={name}>
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Head>{t("tro.whoCame")}</Head>
          <table className="mt-1 w-full text-sm">
            <tbody>
              {d.rows.map((r) => (
                <tr key={r.employee ?? ""} className="border-t border-brand-grey-light first:border-t-0"
                  data-training-attendee={r.employee ?? ""}>
                  <td className="py-1 pr-2">{r.employee_name}</td>
                  <td className="py-1 px-2 text-[11px] text-brand-grey">{r.attendance}</td>
                  <td className="py-1 px-2 text-right tabular-nums">
                    {r.grade ? r.grade.score.toFixed(0) : <span className="text-brand-grey">–</span>}
                  </td>
                  <td className="py-1 pl-2 text-right">
                    {r.grade ? (
                      <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${
                        r.grade.passed ? "bg-green-50 text-status-green" : "bg-red-50 text-status-red"}`}>
                        {r.grade.grade}
                      </span>
                    ) : <span className="text-[11px] text-brand-grey">{t("tro.ungradedShort")}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <Head>{t("tro.skillsMoved")}</Head>
          {d.skills.length === 0 ? (
            <p className="mt-1 text-sm text-brand-grey">{t("tro.noSkillLink")}</p>
          ) : (
            <>
              <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
                {d.skills.map((s) => <span key={s} className="rounded border border-brand-grey-light px-2 py-0.5">{s}</span>)}
              </div>
              <div className="mt-2 text-sm">
                <div className="flex justify-between"><span>{t("tro.measured")}</span>
                  <span className="font-semibold tabular-nums">{d.effectiveness.measured}</span></div>
                <div className="flex justify-between"><span>{t("tro.improved")}</span>
                  <span className="font-semibold tabular-nums">{d.effectiveness.improved}</span></div>
              </div>
              {d.can_record && d.results.graded > 0 && (
                <button onClick={() => uplift.mutate({ training_event: name })} disabled={uplift.isPending}
                  data-training-uplift={name}
                  className="mt-2 rounded bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                  {t("tro.applyUplift")}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {d.feedback_rows.length > 0 && (
        <div className="mt-4 border-t border-brand-grey-light pt-3" data-training-feedback>
          <Head>{t("tro.whatTheySaid")}</Head>
          <div className="mt-1 space-y-1">
            {d.feedback_rows.slice(0, 5).map((f) => (
              <div key={f.name} className="text-[12px]">
                <span className="font-semibold">{f.employee_name}</span>
                <span className="ml-2 text-brand-grey">{f.feedback}</span>
              </div>
            ))}
          </div>
          <p className="mt-1 text-[11px] text-brand-grey">
            {t("tro.responseRate", { pct: d.feedback.response_pct, missing: d.feedback.not_asked })}
          </p>
        </div>
      )}
      {uplift.isError && <div className="mt-2"><ErrorBox error={uplift.error} /></div>}
    </div>
  );
}

const Head = ({ children }: { children: React.ReactNode }) => (
  <div className="text-[11px] font-semibold uppercase tracking-wide text-brand-grey">{children}</div>
);
